// apps/roleforge-app/src/components/chat/InlineActivityIndicator.tsx
//
// Renders a single activity row inline within the chat transcript, the way
// Claude / ChatGPT show "Searching skill catalog…" between message bubbles.

import { useEffect, useState } from 'react';
import type { ActivityItem } from '../../lib/use-activity-stream';

function iconGlyph(icon: ActivityItem['icon']): string {
  if (icon === 'check') return '✓';
  if (icon === 'warn') return '⚠';
  return '⟳';
}

interface Props {
  item: ActivityItem;
}

export function InlineActivityIndicator({ item }: Props) {
  const effectiveIcon = item.kind === 'activity.failed' ? 'spinner' : item.icon;
  const rotating = effectiveIcon === 'spinner' && item.rotatingMessages && item.rotatingMessages.length > 1
    ? item.rotatingMessages
    : null;
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    setMessageIndex(0);
    if (!rotating) return;
    const timer = setInterval(() => {
      setMessageIndex((current) => (current + 1) % rotating.length);
    }, 1800);
    return () => clearInterval(timer);
  }, [item.id, rotating]);

  const tone =
    item.kind === 'activity.completed'
        ? 'text-emerald-700'
        : 'text-slate-500';

  const spinning = effectiveIcon === 'spinner';
  const displayMessage = item.kind === 'activity.failed'
    ? 'Still working on that...'
    : rotating ? rotating[messageIndex] : item.message;

  return (
    <div
      data-testid="inline-activity"
      data-activity-kind={item.kind}
      className={`flex items-center gap-2 px-1 py-1 text-xs ${tone}`}
    >
      <span
        aria-hidden="true"
        className={`font-mono ${spinning ? 'inline-block animate-spin' : ''}`}
      >
        {iconGlyph(effectiveIcon)}
      </span>
      <span className="italic">{displayMessage}</span>
    </div>
  );
}
